import { BillingInterval } from "@flowstepdesign/shared";
import { Check } from "lucide-react";

import { PrimaryCTA } from "./PrimaryCTA";
import { SecondaryCTA } from "./SecondaryCTA";
import { Badge } from "./ui/badge";

export interface PlanFeature {
  label: string;
  entitlement?: string;
  tooltip?: string;
}

export interface PlanCardProps {
  name: string;
  persona: string;
  monthlyPrice: number;
  annualPrice: number;
  billingInterval: BillingInterval;
  features: PlanFeature[];
  ctaLabel: string;
  onCtaClick: (e: React.MouseEvent) => void;
  planId: string;
  currency?: string;
  previousPlan?: string;
  usageMultiplier?: string;
}

export function PlanCard({
  name,
  persona,
  monthlyPrice,
  annualPrice,
  billingInterval,
  features,
  ctaLabel,
  onCtaClick,
  planId,
  currency = "$",
  previousPlan,
  usageMultiplier,
}: PlanCardProps) {
  const currentPrice = billingInterval === "month" ? monthlyPrice : annualPrice;

  return (
    <div className="bg-card relative flex flex-col rounded-2xl border p-8 shadow-sm transition-shadow hover:shadow-md">
      <div className="flex h-full flex-col space-y-6">
        {/* Header */}
        <div>
          <div className="mb-1 flex items-center gap-2">
            <h3 className="font-semibold text-gray-900">{name}</h3>
            {usageMultiplier && (
              <Badge className="border-0 bg-gradient-to-r from-[#0247FE] to-[#06B6D4] text-xs text-white">
                {usageMultiplier} usage limits
              </Badge>
            )}
          </div>
          <p className="text-muted-foreground text-sm">{persona}</p>
        </div>

        {/* Price */}
        <div>
          <div className="space-y-1">
            <div className="flex items-baseline gap-2">
              <span className="text-4xl">
                {currency}
                {currentPrice}
              </span>
              <span className="text-muted-foreground">/month</span>
            </div>
            {billingInterval === "year" && monthlyPrice > 0 && (
              <span className="text-muted-foreground text-xs">
                Billed annually
              </span>
            )}
          </div>
        </div>

        {/* Features */}
        <div className="flex-1 space-y-3">
          {previousPlan && (
            <p className="text-muted-foreground text-sm">
              Everything in {previousPlan}, plus:
            </p>
          )}
          <ul className="space-y-3">
            {features.map((feature, index) => (
              <li key={index} className="flex items-start gap-3">
                <Check className="text-primary mt-0.5 size-4 shrink-0" />
                <span
                  className="text-sm"
                  data-entitlement={feature.entitlement}
                >
                  {feature.label}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* CTA */}
        <div>
          {planId === "free" ? (
            <SecondaryCTA
              className="w-full"
              onClick={onCtaClick}
              data-event="Checkout Started"
              data-plan={planId}
              data-billing={billingInterval}
              data-price={`${currency}${currentPrice}`}
            >
              {ctaLabel}
            </SecondaryCTA>
          ) : (
            <PrimaryCTA
              className="w-full"
              onClick={onCtaClick}
              data-event="Checkout Started"
              data-plan={planId}
              data-billing={billingInterval}
              data-price={`${currency}${currentPrice}`}
            >
              {ctaLabel}
            </PrimaryCTA>
          )}
        </div>
      </div>
    </div>
  );
}
